import { HttpResourceRef } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { UsersProfileFeaturesOnboardingGetResponse, UsersProfileService } from '@customer-portal/api';
import { ActiveMemberState } from '@customer-portal/shared/account/data-access';

import { OnboardingTask, OnboardingTaskKey } from '../model';

@Injectable({ providedIn: 'root' })
export class OnboardingStatusService {
  private readonly usersProfileService = inject(UsersProfileService);
  private readonly activeMemberService = inject(ActiveMemberState);

  private readonly completeTaskResource = this.usersProfileService.usersProfileCompleteOnboardingTask();

  private readonly onUserIdChange = effect(() => {
    this.usersProfileService.usersProfileGetOnboardingTasksParams.userId.set(this.activeMemberService.userId());
  });

  // The generated GET client assumes HAL, but this endpoint returns plain JSON.
  public readonly resource = this.usersProfileService.usersProfileGetOnboardingTasksResource as HttpResourceRef<
    UsersProfileFeaturesOnboardingGetResponse | { content: UsersProfileFeaturesOnboardingGetResponse } | undefined
  >;

  private readonly response = computed((): UsersProfileFeaturesOnboardingGetResponse | undefined => {
    const value = this.resource.value();
    return value && 'content' in value ? value.content : value;
  });

  public readonly isOnboardingInProgress = computed(() => this.response()?.isOnboardingInProgress ?? false);

  public readonly tasks = computed(
    (): readonly OnboardingTask[] =>
      this.response()?.tasks?.map((task) => ({
        key: task.key as OnboardingTaskKey,
        isActive: task.isActive ?? false,
        isCompleted: task.isCompleted ?? false,
        completedAt: task.completedAt ?? null,
      })) ?? [],
  );

  public readonly isCompletingTask = this.completeTaskResource.isLoading;

  public async completeTask(task: OnboardingTaskKey): Promise<boolean> {
    await this.completeTaskResource.invoke({
      userId: signal(this.activeMemberService.userId()),
      body: signal({ task }),
    });

    if (this.completeTaskResource.status() !== 'resolved') {
      return false;
    }

    // The complete response isn't WayChaser-wrapped like the GET resource, so re-fetch instead of
    // hand-constructing a WayChaserResponse to feed into `resource.set()`.
    this.resource.reload();
    return true;
  }
}
